<script setup lang="ts">
import { computed, ref, watch } from "vue";

import { backendOld } from "~/api";
import DropdownMenuWithSelection from "~/components/dumb/DropdownMenuWithSelection.vue";
import SettingsRow from "~/components/settings/SettingsRow.vue";
import SettingsSection from "~/components/settings/SettingsSection.vue";
import { AI_MODEL_TO_DISPLAY_NAME } from "~/constants/aiModel";
import { LANGUAGE_TO_DISPLAY_NAME } from "~/constants/language";
import { AiModel, Language } from "~/shared/enums";
import { usePageStore, useTenantStore, useUserStore } from "~/stores";

const pageStore = usePageStore();
const userStore = useUserStore();
const tenantStore = useTenantStore();

const selectedLanguage = computed(() => LANGUAGE_TO_DISPLAY_NAME[userStore.aiLanguage ?? Language.ENGLISH]);
const languageOptions = computed(() =>
  Object.entries(LANGUAGE_TO_DISPLAY_NAME).map(([language, displayName]) => ({
    title: displayName,
    subtitle: language,
    selected: userStore.aiLanguage === language,
    onClick: () => {
      userStore.aiLanguage = language as Language;
      backendOld.profile.edit("aiLanguage", language);
    },
  }))
);

const modelOptions = computed(() =>
  Object.entries(AI_MODEL_TO_DISPLAY_NAME).map(([model, displayName]) => ({
    title: displayName,
    subtitle: model,
    selected: userStore.aiModel === model,
    onClick: () => {
      userStore.aiModel = model as AiModel;
      backendOld.profile.edit("aiModel", model);
    },
  }))
);

const jobDescription = ref(userStore.jobDescription ?? "");
const aiGuidelines = ref(userStore.aiGuidelines ?? "");
const workspaceDescription = ref(tenantStore.workspaceDescription ?? "");

watch(
  () => tenantStore.workspaceDescription,
  (newVal) => {
    workspaceDescription.value = newVal ?? "";
  },
  { immediate: true }
);

const updateJobDescription = () => {
  const trimmedValue = jobDescription.value.trim();
  userStore.jobDescription = trimmedValue;
  backendOld.profile.edit("jobDescription", trimmedValue);
};

const updateAiGuidelines = () => {
  const trimmedValue = aiGuidelines.value.trim();
  userStore.aiGuidelines = trimmedValue;
  backendOld.profile.edit("aiGuidelines", trimmedValue);
};

const updateWorkspaceDescription = () => {
  const trimmedValue = workspaceDescription.value.trim();
  tenantStore.workspaceDescription = trimmedValue;
  backendOld.workspace.edit("workspaceDescription", trimmedValue);
};

const personalTextareaConfigurations = computed(() => [
  {
    model: jobDescription,
    title: "What do you do?",
    ariaLabel: "Occupation",
    placeholder: "e.g. project manager, software engineer",
    show: pageStore.showAlpha,
    onBlur: updateJobDescription,
  },
  {
    model: aiGuidelines,
    title: "What guidelines should Dart AI follow?",
    ariaLabel: "Guidelines",
    placeholder: "e.g. ask clarifying questions, be concise",
    show: true,
    onBlur: updateAiGuidelines,
  },
]);

const textareaClasses =
  "min-h-20 w-full rounded-md border px-3 py-2 text-sm shadow-sm bg-std text-md border-md focus-ring-std focus-ring-none placeholder:text-vlt hover:border-hvy focus-within:hover:border-primary-base focus:border-primary-base";
</script>

<template>
  <div class="w-full">
    <SettingsSection title="Personal preferences" class="!mt-0" :no-divider="!pageStore.showAlpha">
      <SettingsRow v-if="pageStore.showAlpha" title="Preferred model">
        <DropdownMenuWithSelection :options="modelOptions" border is-settings-mode class="w-48">
          <div class="select-none rounded px-3 py-1.5 text-left text-sm focus-ring-std hover:bg-lt">
            {{ userStore.aiModel }}
          </div>
        </DropdownMenuWithSelection>
      </SettingsRow>

      <SettingsRow title="Preferred Language">
        <DropdownMenuWithSelection :options="languageOptions" border is-settings-mode class="w-48">
          <div class="select-none rounded px-3 py-1.5 text-left text-sm focus-ring-std hover:bg-lt">
            {{ selectedLanguage }}
          </div>
        </DropdownMenuWithSelection>
      </SettingsRow>

      <template v-for="(config, index) in personalTextareaConfigurations" :key="index">
        <div v-if="config.show" class="flex flex-col gap-2">
          <div class="text-sm text-md">{{ config.title }}</div>
          <textarea
            v-model="config.model.value"
            :aria-label="config.ariaLabel"
            :class="textareaClasses"
            :placeholder="config.placeholder"
            @blur="config.onBlur" />
        </div>
      </template>
    </SettingsSection>

    <SettingsSection v-if="pageStore.showAlpha" title="Workspace preferences" no-divider>
      <div class="flex flex-col gap-2">
        <div class="text-sm text-md">What kind of work do you and your team do?</div>
        <textarea
          v-model="workspaceDescription"
          aria-label="Work"
          :class="textareaClasses"
          placeholder="e.g. design agency, healthcare startup..."
          @blur="() => updateWorkspaceDescription()" />
      </div>
    </SettingsSection>
  </div>
</template>
